<template>
  <div class="container">
    <div class="swap-container-item">
      <div class="swap-container">
        <form
          action=""
          autocomplete="off"
          class="swap"
          novalidate
          @submit.prevent="submitForm"
          @keyup.enter="submitForm"
        >
          <div class="swap__header">
            <span>{{ $t('swap.swap') }}</span>
            <div ref="overlayAnchor" class="swap__anchor" />
            <div class="swap__settings settings">
              <div v-if="!isMovement" class="settings__switch">
                <button
                  class="btn-config"
                  @click.prevent="togglePriceChartShown"
                >
                  <img
                    :class="['chart-icon', isPriceChartShown ? 'active' : '']"
                    alt="Chart"
                    src="@/assets/price-chart-icon.svg"
                  />
                </button>
              </div>
              <button
                v-if="!isMovement"
                class="settings__frontrun btn-config"
                type="button"
                @click.prevent="toggleFrontrunOverlay"
              >
                <img
                  :alt="$t('common.security')"
                  class="frontrun-icon"
                  src="@/assets/frontrun-icon.svg"
                  width="24"
                />
              </button>
              <FrontrunOverlay ref="frontrunOverlay" />
              <CopyLink :link="urlToCopy" class="settings__copy-link" />
              <button
                class="settings__config btn-config"
                type="button"
                @click.prevent="toggleConfig"
              >
                <i class="pi pi-cog config-icon" />
              </button>
              <TxSettingsOverlay
                ref="settingsOverlay"
                v-model:is-default="swapStore.slippageIsDefault"
                v-model:slippage="swapStore.slippage"
                :from-token="swapStore.fromCurrency.token"
                :to-token="swapStore.toCurrency.token"
              />
            </div>
          </div>
          <div class="swap__row">
            <SwapInput
              :should-be-updated-flag="requireUpdateFromInput"
              autofocus
              mode="from"
              @click:select-token="isOpenedSelectToken = true"
              @update:model="debouncedUpdateModelValue($event, 'from')"
              @click:max-balance="handleMaxBalance($event, 'from')"
              @focus:custom="setLocalLastChanges('from')"
              @input:custom="handleInput"
            />
          </div>
          <div class="swap__row swap__toggle">
            <InputToggle
              :disabled="isUpdatingPriceImpact || isToggling || isBusy"
              :is-visible="!toCurrency.token"
              @swap-inputs="toggleSwap"
            />
          </div>
          <div class="swap__row">
            <SwapInput
              :should-be-updated-flag="requireUpdateToInput"
              mode="to"
              @click:select-token="isOpenedSelectToken = true"
              @update:model="debouncedUpdateModelValue($event, 'to')"
              @click:max-balance="handleMaxBalance($event, 'to')"
              @focus:custom="setLocalLastChanges('to')"
              @input:custom="handleInput"
            />
          </div>
          <TransitionGroup
            :duration="200"
            appear
            class="swap__group"
            mode="out-in"
            name="fade"
            tag="div"
          >
            <div
              v-if="
                tokensChosen &&
                !fullCurveOfDefaultPool &&
                smartRouterMode !== 'default'
              "
              :key="`${tokensChosen && !fullCurveOfDefaultPool}-curve-warning`"
              class="swap__row"
            >
              <PInlineMessage
                :class="'curve-warning'"
                class="mt-1"
                severity="warn"
                >{{ $t('swap.curveWarning') }}</PInlineMessage
              >
            </div>
            <div
              v-else-if="currentModeError.state === 'amount too low'"
              :key="`${currentModeError.state}-amount-warning`"
              class="swap__row"
            >
              <PInlineMessage class="mt-1" severity="warn">{{
                $t('errors.amountTooLowDescription', {
                  input:
                    interactiveField === 'from'
                      ? $t('common.input')
                      : $t('common.output'),
                  output:
                    interactiveField === 'from'
                      ? $t('common.output')
                      : $t('common.input'),
                })
              }}</PInlineMessage>
            </div>
            <div
              v-show="canSwitchCurve"
              :key="`${canSwitchCurve}-curve-switch`"
              class="swap__row"
            >
              <CurveSwitch
                v-if="smartRouterMode === 'direct' && version !== VERSION_1"
                :curve="swapStore.curve"
                :version="version"
                mode="swap"
              />
            </div>
            <div
              v-if="
                !isPoolAbsence &&
                swapStore.convertRate &&
                swapStore.toCurrency.amount &&
                swapStore.fromCurrency.amount
              "
              :key="`${swapStore.toCurrency.token}-${swapStore.fromCurrency.token}-swap-info`"
              class="swap__row"
            >
              <SwapInfo />
            </div>
            <div
              v-show="canSwitchContract && smartRouterMode === 'direct'"
              :key="`${canSwitchContract}-contract-switch`"
              class="swap__row swap__contract-switch"
            >
              <SwapContractSwitch
                :version="version"
                :versions="availableVersions"
                @change:version="(ver: typeof VERSION_0 | typeof VERSION_0_5 | typeof VERSION_1 | undefined) => setVersion(ver)"
              />
            </div>
            <template v-if="smartRouterMode === 'direct'">
              <TransitionGroup :duration="200" appear mode="out-in" name="fade">
                <BinStepInfo
                  v-if="directBinStep"
                  :key="`${directBinStep}-bin-step`"
                  :bin-step="directBinStep"
                />
                <div
                  v-else-if="
                    swapStore.curve &&
                    version !== VERSION_1 &&
                    canSwitchContract
                  "
                  :key="`${canSwitchCurve}-curve-info`"
                  class="swap__row swap__curve-info"
                >
                  <SwapCurveInfo
                    :type="getShortCurveFromFull(swapStore.curve) || ''"
                  />
                </div>
              </TransitionGroup>
            </template>
          </TransitionGroup>
          <div class="swap__row">
            <p-button
              v-if="!account"
              class="swap__button is-connect p-button-primary"
              tabindex="5"
              type="submit"
            >
              <span>{{ $t('common.connectWallet') }}</span>
            </p-button>
            <p-button
              v-else
              :class="[
                { 'p-disabled': buttonState.disabled },
                priceImpactClass,
              ]"
              :disabled="buttonState.disabled"
              class="swap__button"
              tabindex="5"
              type="submit"
            >
              <span>{{ buttonState.text }}</span>
            </p-button>
          </div>
        </form>
      </div>
      <slot name="swappingRoute" />

      <ReservesContainer
        v-if="smartRouterMode === 'direct'"
        :key="reservesContainerKey"
        :need-to-update-reserves="needToUpdateReserves"
        type="swap"
      />
    </div>

    <SideDrawer
      v-model:visible="isOpenedSelectToken"
      @hide="isOpenedSelectToken = false"
    >
      <template #content>
        <SelectTokenSideDrawer
          @close="isOpenedSelectToken = !isOpenedSelectToken"
          @update:action-token="handleUpdateActionToken"
        />
      </template>
    </SideDrawer>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  inject,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useDialog } from 'primevue/usedialog';
import { useRoute } from 'vue-router';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import { useWalletPlugin } from '@/composables/useWalletPlugin';
import { useDebounceFn } from '@vueuse/core';
import { watchDebounced } from '@vueuse/shared';
import { v4 as uuid } from 'uuid';
import { EMixpanelEventNames, EMixpanelProvideKeys } from '@/types/enums';

import { TCurveType, TCustomEvent } from '@/types';
import { TokenFiledType } from '@/types/tokens';

// services, constants, utils, etc
import router from '@/router';
import { d, decimalsMultiplier, deepUnref } from '@/utils';
import { getCurve, getShortCurveFromFull } from '@/utils/contracts';
import { getCopyUrl } from '@/composables/getUrlToCopyPool';
import {
  ChainsEnum,
  CORRECT_CHAIN,
  VERSION_0,
  VERSION_0_5,
  VERSION_1,
} from '@/constants';
import {
  CURVE_SHORT_NAMES,
  CURVE_STABLE,
  CURVE_STABLE_V05,
  CURVE_UNSTABLE,
  CURVE_UNSTABLE_V05,
  TCurveShortNames,
} from '@/constants/networks';
import {
  showImportTokenDialog,
  showInstallMobileAppDialogByOS,
  showPriceImpactWarningDialog,
  showSwapConfirmDialog,
  TOnCloseCb,
} from '@/composables/useDialogService';
import { useCurrentAccountBalance } from '@/composables/useAccountBalance';
import {
  useDialogStore,
  usePoolsStore,
  usePoolsV1Store,
  useStore,
  useSwapStore,
  useTokensStore,
} from '@/store';
import { usePriceChart } from '@/composables/usePriceChart';
import {
  getAmountInteger,
  getTokenDecimal,
} from '@/composables/useInputServices';
import { useI18n } from '@/composables/useI18n';

// components
import { SwapContractSwitch } from '@/components/UI/ContractSwitch';
import { CurveSwitch } from '@/components/UI/CurveSwitch';
import { FrontrunOverlay } from '@/components/UI/FrontrunOverlay';
import { ReservesContainer } from '@/components/UI/ReservesContainer';
import { SideDrawer } from '@/components/UI/SideDrawer';
import { TxSettingsOverlay } from '@/components/UI/TxSettingsOverlay';
import { CopyLink } from '@/components/UI/CopyLink';
import SwapCurveInfo from '@/components/UI/CurveInfo/SwapCurveInfo.vue';
import { BinStepInfo } from '@/components/UI/BinStep';
import InputToggle from '@/components/UI/InputToggle/InputToggle.vue';
import SelectTokenSideDrawer from '@/components/Pools/SelectTokenSideDrawer.vue';
import SwapInfo from '@/components/Swap/SwapInfo.vue';
import SwapInput from '@/components/Swap/SwapInput.vue';
import { IPersistedPool, IPersistedPoolV1 } from '@/types/pools';
import { usePoolSwitcherByCurveAndVersion } from '@/composables/usePoolSwitcherByCurveAndVersion';
import { cloneDeep } from 'lodash';

const { t } = useI18n();

const isMovement = computed(() =>
  [ChainsEnum['movement-testnet'], ChainsEnum['movement-mainnet']].includes(
    CORRECT_CHAIN.id,
  ),
);

//inits
const track: any = inject(EMixpanelProvideKeys.Track);
const requireUpdateInput = ref<TokenFiledType>();
const { determinePairSettings } = usePoolSwitcherByCurveAndVersion();

const { isPriceChartShown } = usePriceChart();

const tokensStore = useTokensStore();
const dialogStore = useDialogStore();
const dialog = useDialog();
const route = useRoute();

const { account } = useWalletPlugin();

const mainStore = useStore();
const poolsStore = usePoolsStore();
const poolsV1Store = usePoolsV1Store();
const { poolsByTokenSymbol } = storeToRefs(poolsV1Store);

const swapStore = useSwapStore();
const {
  fromCurrency,
  toCurrency,
  swapFunctionName,
  isBusy,
  isSmartRouterActive,
  isUpdatingPriceImpact,
  isPoolAbsence,
  interactiveField,
  directCoinTransfers,
  smartRouterMode,
  lastInteractiveField,
  currentModeError,
  version,
  curve,
  directBinStep,
} = storeToRefs(swapStore);
const {
  pauseSmartRouter,
  resumeSmartRouter,
  setCurve,
  setVersion,
  setIsUpdatingPriceImpact,
} = swapStore;

const isOpenedSelectToken = ref(false);
const isToggling = ref(false);
const settingsOverlay = ref();
const frontrunOverlay = ref();
const overlayAnchor = ref();

const routeToToken = ref<string | undefined>();
const routeFromToken = ref<string | undefined>();

const tempFindPool: Array<{
  curve: TCurveType;
  version: typeof VERSION_0 | typeof VERSION_0_5;
}> = [
  { curve: 'stable', version: VERSION_0 },
  { curve: 'stable', version: VERSION_0_5 },
  { curve: 'unstable', version: VERSION_0 },
  { curve: 'unstable', version: VERSION_0_5 },
];

const currentPools = ref<
  {
    pool: IPersistedPool | undefined;
    version: typeof VERSION_0 | typeof VERSION_0_5;
    curveType: TCurveType;
    isDefault: boolean;
  }[]
>([]);

const debouncedUpdateModelValue = useDebounceFn(handleUpdateModelAmount, 2000);

onBeforeMount(() => {
  document.addEventListener('visibilitychange', handleTabVisibilityChange);
  if (tokensStore.isReady) {
    updateQueryParameters();
  }
  if (!isSmartRouterActive.value) {
    resumeSmartRouter();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleTabVisibilityChange);
  if (isSmartRouterActive.value) {
    pauseSmartRouter();
  }
});

// computed

const priceImpactClass = computed(() => {
  return swapStore.priceImpactState === 'normal'
    ? 'p-button-primary'
    : swapStore.priceImpactState === 'warning'
    ? 'p-button-warning_custom'
    : 'p-button-alert';
});

const stableCurve = computed(() => getCurve('stable', version.value));
const unstableCurve = computed(() => getCurve('unstable', version.value));

const requireUpdateFromInput = computed(() => {
  return (
    requireUpdateInput.value == 'from' || requireUpdateInput.value == undefined
  );
});

const requireUpdateToInput = computed(() => {
  return (
    requireUpdateInput.value == 'to' || requireUpdateInput.value == undefined
  );
});

const reservesContainerKey = computed(() => {
  return `${fromCurrency.value.token}-${toCurrency.value.token}-${needToUpdateReserves.value}-${fromCurrency.value.reserve}-${toCurrency.value.reserve}-reserve-container`;
});

/**
 * We call "default" pools - pools from the coins-registry.
 * Returns false if we don't know about the pool with the selected token pair.
 */
const fullCurveOfDefaultPool = computed(() => {
  // Now all known pools have version 0
  const predefinedCurveV0 = poolsStore.getCurveType(
    swapStore.fromCurrency?.token,
    swapStore.toCurrency?.token,
    VERSION_0,
  );

  const predefinedCurveV05 = poolsStore.getCurveType(
    swapStore.fromCurrency?.token,
    swapStore.toCurrency?.token,
    VERSION_0_5,
  );

  const hasDefaultPoolStableCurveV0 = [CURVE_STABLE, CURVE_STABLE_V05].includes(
    '' + predefinedCurveV0,
  );

  const hasDefaultPoolStableCurveV05 = [
    CURVE_STABLE,
    CURVE_STABLE_V05,
  ].includes('' + predefinedCurveV05);

  const hasDefaultPoolUnstableCurveV0 = [
    CURVE_UNSTABLE,
    CURVE_UNSTABLE_V05,
  ].includes('' + predefinedCurveV0);

  const hasDefaultPoolUnstableCurveV05 = [
    CURVE_UNSTABLE,
    CURVE_UNSTABLE_V05,
  ].includes('' + predefinedCurveV05);

  if (
    (predefinedCurveV0 || predefinedCurveV05) &&
    (hasDefaultPoolStableCurveV0 || hasDefaultPoolStableCurveV05)
  ) {
    return stableCurve.value;
  }

  if (
    (predefinedCurveV0 || predefinedCurveV05) &&
    (hasDefaultPoolUnstableCurveV0 || hasDefaultPoolUnstableCurveV05)
  ) {
    return unstableCurve.value;
  }

  return false;
});

const fromTokenDecimal = computed(() =>
  getTokenDecimal(fromCurrency.value.token),
);
const toTokenDecimal = computed(() => getTokenDecimal(toCurrency.value.token));

const fromBalance = useCurrentAccountBalance(
  computed(() => fromCurrency.value?.token),
);
const toBalance = useCurrentAccountBalance(
  computed(() => toCurrency.value?.token),
);

const tokensChosen = computed(
  () => !!fromCurrency.value.token && !!toCurrency.value.token,
);

const currentPairSettings = computed(() => {
  if (
    !fromCurrency.value.token ||
    !toCurrency.value.token ||
    currentPools.value.length === 0
  ) {
    return {
      showSwitcherForCurve: false,
      showSwitcherForContract: false,
      preselectCurve: undefined,
      preselectContract: undefined,
    };
  }

  const result = determinePairSettings(deepUnref(currentPools.value));

  if (result?.pool) {
    poolsStore.setPool(result.pool);
  }

  return result;
});

const availableVersions = computed(() => {
  const _currentPools = currentPools.value
    .filter((item) => item.pool && item.isDefault)
    .map((item) => {
      const _versions = {
        [VERSION_0]: 'v0',
        [VERSION_0_5]: 'v0.5',
        [VERSION_1]: 'v1.0',
      };
      return {
        label: _versions[item.version] || `v${item.version}`,
        value: item.version,
      };
    });
  return uniqBy(_currentPools, 'value');
});

const canSwitchCurve = computed(() => {
  return currentPairSettings.value.showSwitcherForCurve;
});

const canSwitchContract = computed(() => {
  return currentPairSettings.value.showSwitcherForContract;
});

const buttonState = computed(() => {
  if (isUpdatingPriceImpact.value) {
    return { disabled: true, text: '...' };
  }

  if (currentModeError.value.state) {
    return { disabled: true, text: currentModeError.value.text };
  }

  if (!fromCurrency.value.token || !toCurrency.value.token) {
    return { disabled: true, text: t('pools.selectToken') };
  }

  if (isPoolAbsence.value) {
    return { disabled: true, text: t('pools.poolIsNotCreated') };
  }

  if (!fromCurrency.value.amount || !toCurrency?.value.amount) {
    return { disabled: true, text: t('pools.enterAnAmount') };
  }

  if (!swapFunctionName.value) {
    return { disabled: true, text: t('swap.notFound') };
  }

  let amount = d(fromCurrency.value.amount);
  let balance = d(fromBalance.balance.value);

  if (interactiveField.value === 'to') {
    amount = amount.plus(amount.mul(swapStore.slippage));
  }

  if (fromCurrency.value.token === mainStore.defaultToken.value) {
    // TODO: see it and maybe use gas estimation value
    // Bind 2000 tokens for gas
    balance = balance.minus(2000);
  }

  const haveBalance = amount.lte(balance);
  if (!haveBalance) {
    return {
      disabled: true,
      text: t('pools.insufficientBalance', [fromBalance.symbol.value]),
    };
  }

  if (directCoinTransfers.value) {
    return {
      disabled: false,
      text: t('swap.swap'),
    };
  }

  if (
    !toBalance.isExists.value &&
    !(toBalance.isFetching.value && toBalance.isFirstFetching.value)
  ) {
    return {
      disabled: false,
      text: t('swap.registerAndSwap', [toBalance.symbol.value]),
    };
  }

  return {
    disabled: false,
    text: t('swap.swap'),
  };
});

const urlToCopy = computed(() => {
  const curve = swapStore.curve as unknown;
  if (
    !curve ||
    (typeof curve === 'string' &&
      (curve.toLowerCase() === 'stable' || curve.toLowerCase() === 'unstable'))
  ) {
    return '';
  }
  return getCopyUrl({
    fromToken: fromCurrency.value.token,
    toToken: toCurrency.value.token,
    version: version.value,
    curve: getShortCurveFromFull(swapStore.curve),
    action: 'swap',
    fullPath: route.fullPath,
  });
});

watch(
  () => [
    fromCurrency.value.token,
    toCurrency.value.token,
    smartRouterMode.value,
  ],

  async (newValue, oldValue) => {
    const [newFrom, newTo, _, newSmartRouterMode] = newValue as [
      newFrom: string | undefined,
      newTo: string | undefined,
      newVersion: 0 | 1 | 0.5 | undefined,
      newSmartRouterMode: 'default' | 'direct',
    ];
    const [oldFrom, oldTo, _oldVersion, _oldSmartRouterMode] = (oldValue ||
      []) as [
      oldFrom: string | undefined,
      oldTo: string | undefined,
      _oldVersion: 0 | 1 | 0.5 | undefined,
      _oldSmartRouterMode: 'default' | 'direct',
    ];

    if (
      !newFrom ||
      !newTo ||
      (oldFrom === newTo && oldTo == newFrom) ||
      newSmartRouterMode === 'default'
    ) {
      return;
    }

    const promises: Promise<{
      pool: IPersistedPool | undefined;
      version: number;
      curveType: TCurveType;
    }>[] = [];

    const savePool = false;

    // Create promises for each combination of curve and version

    currentPools.value = [];

    tempFindPool.forEach((item) => {
      promises.push(
        poolsStore
          .getPool(
            newFrom,
            newTo,
            getCurve(item.curve, item.version as number),
            item.version,
            savePool,
          )
          .then((pool) => ({
            pool,
            version: item.version,
            curveType: item.curve,
            isDefault: pool?.isDefault || false,
          })),
      );
    });

    const results: {
      pool: IPersistedPool | IPersistedPoolV1 | undefined;
      version: number;
      curveType?: TCurveType;
      isDefault?: boolean;
    }[] = await Promise.all(promises);
    const v1Pools = poolsByTokenSymbol.value(newFrom, newTo).map((pool) => ({
      pool,
      version: VERSION_1,
      isDefault: true,
    }));
    results.push(...v1Pools);

    Object.assign(currentPools.value, results);
  },
  {
    immediate: true,
  },
);

watchDebounced(
  [canSwitchContract, canSwitchCurve, currentPools],
  () => {
    if (!fromCurrency.value.token || !toCurrency.value.token) return;
    setVersion(currentPairSettings.value.preselectContract);
    setCurve(
      getCurve(
        currentPairSettings.value.preselectCurve,
        currentPairSettings.value.preselectContract,
      ),
    );
  },
  {
    immediate: true,
    deep: true,
    debounce: 200,
  },
);

watch(
  () => [route.query, tokensStore.isReady],
  () => {
    if (!tokensStore.isReady) return;
    updateQueryParameters();
  },
);

watchDebounced(
  () => [routeFromToken.value, routeToToken.value, tokensStore.isReady],

  () => {
    if (!tokensStore.isReady) return;
    if (routeFromToken.value) {
      importFromToken(
        cloneDeep(routeFromToken.value),
        cloneDeep(routeToToken.value),
      );
    } else if (routeToToken.value) {
      importToToken(cloneDeep(routeToToken.value));
    }
  },
  {
    debounce: 400,
  },
);

// funcs

async function importFromToken(token: string, fromToken?: string) {
  if (!token) return;
  const _token = token.replace(/^0x0+/, '0x');

  const fromTokenParam = await tokensStore.asyncGetToken(_token);
  if (!fromTokenParam) {
    if (!tokensStore.isReady) return;

    showImportTokenDialog(
      dialog,
      { mode: 'from', store: 'swap', token: _token },
      importTokenDialogCloseCb,
    );
  } else {
    swapStore.fromCurrency.token = fromTokenParam.type;
    await importToToken(fromToken || (routeToToken.value as string));
  }
}

async function importToToken(token: string) {
  if (!token) return;
  const _token = token.replace(/^0x0+/, '0x');

  const toTokenParam = await tokensStore.asyncGetToken(_token);
  if (!toTokenParam) {
    if (!tokensStore.isReady) return;
    showImportTokenDialog(
      dialog,
      { mode: 'to', store: 'swap', token: _token },
      importTokenDialogCloseCb,
    );
  } else {
    swapStore.toCurrency.token = toTokenParam.type;
  }
}
function submitForm(e: Event) {
  const isNextButton = (e.target as HTMLElement)?.enterKeyHint === 'next';
  const isSubmitDisabled = buttonState.value.disabled;
  const accountValue = account.value;
  const cancelEvent = Boolean(
    (isNextButton || isSubmitDisabled) && accountValue,
  );
  if (cancelEvent) return;

  if (!accountValue) {
    onConnectWallet();
    return;
  }

  track(EMixpanelEventNames.SwapButtonClicked);
  showSwapDialog();
}

function onConnectWallet() {
  showInstallMobileAppDialogByOS(dialog);
}

async function toggleSwap() {
  isToggling.value = true;
  const from = { ...fromCurrency.value };
  const to = { ...toCurrency.value };
  const mode = (lastInteractiveField.value + '') as 'from' | 'to';

  await swapStore.toggleCurrencies(from, to, mode, isToggling);
  requireUpdateInput.value = undefined;
}

function showSwapDialog() {
  const isShowSwapWarningDialog =
    swapStore.priceImpactState === 'warning' ||
    swapStore.priceImpactState === 'alert';

  if (isShowSwapWarningDialog) {
    const data = {
      priceImpact: swapStore.priceImpact,
    };
    showPriceImpactWarningDialog(dialog, data, priceImpactDialogCloseCb);
    return;
  }

  showSwapConfirmDialog(dialog, updateReserves);
}

const needToUpdateReserves = ref('');

const updateReserves = () => {
  //need to update reserves. Some uniq property so pass to Reserves Container.
  needToUpdateReserves.value = uuid();
};

const priceImpactDialogCloseCb: TOnCloseCb = (options) => {
  const action: 'swap' | 'cancel' | undefined = options?.data?.type;
  if (action === 'swap') {
    showSwapConfirmDialog(dialog, updateReserves);
  }

  return;
};

const importTokenDialogCloseCb: TOnCloseCb = (options) => {
  dialogStore.resetDialogState();

  if (options?.data.mode === 'from') {
    routeFromToken.value = undefined;
    importToToken('');
    return;
  }

  routeToToken.value = undefined;
};

function toggleConfig(e: Event) {
  settingsOverlay?.value &&
    settingsOverlay.value.toggle(e, overlayAnchor.value);
}

function toggleFrontrunOverlay(e: Event) {
  frontrunOverlay?.value &&
    frontrunOverlay.value.toggle(e, overlayAnchor.value);
}

function togglePriceChartShown() {
  isPriceChartShown.value = !isPriceChartShown.value;
}

function handleMaxBalance(event: TCustomEvent, type: TokenFiledType) {
  requireUpdateInput.value = undefined;

  const { value } = event;
  const decimal =
    type == 'from' ? fromTokenDecimal.value : toTokenDecimal.value;

  event.value = +d(value).div(decimalsMultiplier(decimal)).toFixed(decimal);

  handleUpdateModelAmount(event, type);
}

function handleUpdateModelAmount(event: TCustomEvent, type: TokenFiledType) {
  interactiveField.value = type;

  const tokenDecimals =
    type == 'from' ? fromTokenDecimal.value : toTokenDecimal.value;
  const tokenModel = type == 'from' ? fromCurrency : toCurrency;

  const { value } = event;

  if (isNil(tokenDecimals)) return;

  tokenModel.value.amount = getAmountInteger(value, tokenDecimals);
}

function setLocalLastChanges(type: TokenFiledType) {
  requireUpdateInput.value = type;
}

function handleInput(event: TCustomEvent) {
  requireUpdateInput.value = event.mode == 'from' ? 'to' : 'from';
}

async function handleUpdateActionToken() {
  setIsUpdatingPriceImpact(smartRouterMode.value === 'default');
  isOpenedSelectToken.value = !isOpenedSelectToken.value;
  requireUpdateInput.value = undefined;
}

function updateQueryParameters() {
  let _version: typeof VERSION_0 | typeof VERSION_0_5 = VERSION_0;
  routeToToken.value = route.query.to as string;
  routeFromToken.value = route.query.from as string;

  if (route.query?.version) {
    const { version: queryVersion } = route.query as { version: string };
    setTimeout(() => {
      _version = queryVersion == `${VERSION_0_5}` ? VERSION_0_5 : VERSION_0;
      setVersion(_version);
    }, 500);
  }
  if (
    typeof route.query?.curve === 'string' &&
    CURVE_SHORT_NAMES.indexOf(route.query.curve as TCurveShortNames) !== -1
  ) {
    const shortCurveName = route.query.curve as TCurveShortNames;
    setTimeout(() => {
      setCurve(getCurve(shortCurveName, _version));
    }, 800);
  }
  setTimeout(
    () =>
      router.replace({
        query: undefined,
      }),
    1000,
  );
}

function handleTabVisibilityChange() {
  if (document.visibilityState === 'visible') {
    resumeSmartRouter();
  } else {
    pauseSmartRouter();
  }
}
</script>

<style lang="scss" scoped>
$mobile-resolution: 767px;

::v-deep(.curve-warning) {
  .p-inline-message-icon {
    display: none;
  }
}

.btn-config {
  background: transparent;
  border: none;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  padding: 0;
  justify-content: center;

  .config-icon {
    width: 100%;
    color: #d4c4ed;
    align-self: center;
    transform: rotate(25deg);
    transition: color 0.3s ease;
    font-size: 16px;
  }

  .chart-icon {
    width: 22px;
    opacity: 0.2;
  }

  .chart-icon.active {
    opacity: 1;
  }

  &:hover {
    .config-icon {
      color: var(--primary-color);
    }
  }
}

.container {
  max-width: 480px;
  flex-direction: column;
  @media (max-width: 500px) {
    max-width: 100%;
    width: 100%;
  }
}

.swap-container {
  padding: 0.5rem;
  border-radius: 24px;
  min-height: 200px;
  color: white;
  background: var(--surface-card);
  box-shadow: rgb(0 0 0 / 1%) 0 0 1px, rgb(0 0 0 / 4%) 0 4px 8px,
    rgb(0 0 0 / 4%) 0 16px 24px, rgb(0 0 0 / 1%) 0 24px 32px;
  margin-bottom: 20px;
}

.swap {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  position: relative;
  &__header {
    padding: 0.3rem 1.5rem 0.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    font-family: var(--font-family);
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-color);

    // Winter Christmas hat
    //&::before {
    //  position: absolute;
    //  content: url(../../assets/snow.svg);
    //  top: -10px;
    //  right: 34px;
    //}
  }

  &__anchor {
    height: 1px;
    max-width: 1px;
    margin-left: 120px;
  }

  .slippage-input {
    text-decoration: underline;
    text-decoration-style: dotted;
  }

  &__row {
    margin-left: 0.2rem;
  }

  &__group {
    position: relative;
  }

  &__curve-info,
  &__contract-switch {
    margin-top: 0.5rem;
  }

  &__toggle {
    position: relative;
    margin-top: -0.5rem;
  }

  &__settings {
    display: flex;
    gap: 12px;
  }

  &-toggle {
    position: absolute;
    padding: 4px;
    border-radius: 12px;
    height: 32px;
    width: 32px;
    margin-top: -12px;
    margin-bottom: -18px;
    left: calc(50% - 16px);
    background-color: #24243a;
    border: 6px solid var(--surface-card);
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      font-size: 12px;
      color: var(--surface-400);
    }
  }

  &__button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    height: 54px;
    font-size: 1.1rem;
    border-radius: 12px;
    font-weight: 500;

    &_warning {
      background: linear-gradient(89.82deg, #e57106 0.08%, #ee5d0c 99.85%);
    }

    &_alert {
      background: linear-gradient(89.82deg, #d75050 0.08%, #c62828 99.85%);
    }
  }

  &__button.swap__row {
    margin-top: 1rem;
  }

  &__info {
    transition: all 0.3s ease;
  }
}

.carousel {
  &__header {
    padding: 1rem 1.25rem 0.5rem;

    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
}

.settings {
  &__switch {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.switch {
  &__label {
    font-size: 11px;
    font-weight: 700;
    line-height: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
}

.fade-move {
  transition: color 200ms ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: color 200ms ease;
}
.fade-leave-active {
  position: absolute;
}

.fade-enter-from,
.fade-leave-to {
  filter: grayscale(1);
}
</style>
